/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Drawer, IModuleProps, INodeProps, KeyCodes } from '@msdyn365-commerce-modules/utilities';
import { SearchArea } from '@msdyn365-commerce/retail-proxy';
import { IDXC_StoreLocations } from '../../data-actions/generated/DataServiceEntities.g';
import { getStoreLocationsAction, GetStoreLocationsInput } from '../../data-actions/store-locations/get-store-locations';
import { ICittaStoreLocationsData } from './citta-store-locations.data';
import { ICittaStoreLocationsProps } from './citta-store-locations.props.autogenerated';

interface ICittaStoreLocationsState {
    storeCategoryId: string;
    allStores: IDXC_StoreLocations[] | undefined;
    nearMeStores: IDXC_StoreLocations[] | undefined;
    stores: IDXC_StoreLocations[] | undefined;
    storeType: string;
    storeTypeLabel: string;
    storeHeadingLabel: string;
    openFirstDrawerComp: boolean;
}

export interface ICittaStoreLocationsViewProps extends ICittaStoreLocationsProps<ICittaStoreLocationsData> {
    CittaStoresShow: boolean;
    CittaStores: IModuleProps;
    StoreTypeHeading: INodeProps;
    StoreHeadingLabel: INodeProps;
    StoreHeadingLabelValue: string;
    CittaStoreLocations: INodeProps;
    CittaStoresList: React.ReactNode;
    ViewStoresToggle: INodeProps;
    ToggleStoreName: React.ReactNode;
}

/**
 *
 * CittaStoreLocations component
 * @extends {React.PureComponent<ICittaStoreLocationsProps<ICittaStoreLocationsData>>}
 */
class CittaStoreLocations extends React.PureComponent<ICittaStoreLocationsProps<ICittaStoreLocationsData>, ICittaStoreLocationsState> {
    // tslint:disable-next-line: typedef
    private resources = {
        store_stockistOfHeading: this.props.resources.store_stockistOfHeading || 'Stockists of',
        store_storeHoursHeading: this.props.resources.store_storeHoursHeading || 'Store Hours',
        store_storeHoursMonday: this.props.resources.store_storeHoursMonday || 'Monday',
        store_storeHoursTuesday: this.props.resources.store_storeHoursTuesday || 'Tuesday',
        store_storeHoursWednesday: this.props.resources.store_storeHoursWednesday || 'Wednesday',
        store_storeHoursThursday: this.props.resources.store_storeHoursThursday || 'Thursday',
        store_storeHoursFriday: this.props.resources.store_storeHoursFriday || 'Friday',
        store_storeHoursSaturday: this.props.resources.store_storeHoursSaturday || 'Saturday',
        store_storeHoursSunday: this.props.resources.store_storeHoursSunday || 'Sunday',
        store_storeHoursClosed: this.props.resources.store_storeHoursClosed || 'Closed',
        viewClosestStoresLabel: this.props.resources.viewClosestStoresLabel || 'VIEW CLOSEST STORES',
        viewAllStoresLabel: this.props.resources.viewAllStoresLabel || 'VIEW ALL STORES',
        yourClosestStoresLabel: this.props.resources.yourClosestStoresLabel || 'Your Closest Store',
        cittaAllStoresLabel: this.props.resources.cittaAllStoresLabel || 'All Città Stores'
    };

    constructor(props: ICittaStoreLocationsProps<ICittaStoreLocationsData>) {
        super(props);
        this.state = {
            storeCategoryId: '',
            allStores: [],
            nearMeStores: [],
            stores: [],
            storeType: 'NEAR_ME',
            storeTypeLabel: this.resources.viewAllStoresLabel,
            storeHeadingLabel: this.resources.yourClosestStoresLabel,
            openFirstDrawerComp: true
        };
        this._changeStoreCategoryItem = this._changeStoreCategoryItem.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        const inputObj: SearchArea = {};
        if (navigator && navigator.geolocation) {
            // tslint:disable-next-line: no-this-assignment
            const that = this;
            navigator.geolocation.getCurrentPosition(
                position => {
                    inputObj.Latitude = position.coords.latitude ? position.coords.latitude : 0; // -36.84403
                    inputObj.Longitude = position.coords.longitude ? position.coords.longitude : 0; // 174.76008
                    inputObj.Radius = 100; // position.coords.accuracy;
                    inputObj.DistanceUnitValue = 0; // position.coords.altitudeAccuracy!;
                    // tslint:disable-next-line: no-floating-promises
                    getStoreLocationsAction(new GetStoreLocationsInput(inputObj), this.props.context.actionContext).then(
                        (result) => {
                            this.setState({
                                allStores: result.storeLocations,
                                nearMeStores: result.nearMeStoreLocations,
                                stores: result.nearMeStoreLocations
                            });
                        });
                },
                error => {
                    if (error.code === error.PERMISSION_DENIED) {
                        inputObj.Latitude = 0; // -36.84403
                        inputObj.Longitude = 0; // 174.76008
                        inputObj.Radius = 100; // position.coords.accuracy;
                        inputObj.DistanceUnitValue = 0; // position.coords.altitudeAccuracy!;
                        getStoreLocationsAction(new GetStoreLocationsInput(inputObj), that.props.context.actionContext).then(
                            (result) => {
                                that.setState({
                                    allStores: result.storeLocations,
                                    nearMeStores: result.nearMeStoreLocations,
                                    stores: result.nearMeStoreLocations
                                });
                            })
                            .catch(() => {
                                console.log('No data');
                            });
                    }
                });
        }
    }

    public render(): JSX.Element | null {
        const CittaStoreLocationsViewProps = {
            ...this.props,
            CittaStoresShow: this.state.stores && this.state.stores.length > 0,
            CittaStores: {
                moduleProps: this.props,
                className: 'citta-stores'
            },
            StoreTypeHeading: {
                className: 'store-type-heading'
            },
            StoreHeadingLabel: {
                className: 'store-heading-label',
                tag: 'p'
            },
            StoreHeadingLabelValue: this.state.storeHeadingLabel,
            CittaStoreLocations: {
                className: 'citta-store-locations',
                role: 'menu'
            },
            CittaStoresList: this._renderStoresList(),
            ViewStoresToggle: {
                className: 'view-stores-toggle'
            },
            ToggleStoreName: this._renderToggleStoreName()
        };
        return this.props.renderView(CittaStoreLocationsViewProps) as React.ReactElement;
    }

    private _renderToggleStoreName(): React.ReactNode {
        return (
            <a
                tabIndex={0}
                className='toggle-store-name'
                role='link'
                onKeyDown={this._toggleName()}
                onClick={this._toggleName()}
            >{this.state.storeTypeLabel}
            </a>
        );
    }

    // tslint:disable-next-line: no-any
    private _toggleName = () => (event: any) => {
        if (event.type === 'click' || (event.type === 'keydown' && event.keyCode === KeyCodes.Enter)) {
            if (this.state.storeType === 'NEAR_ME') {
                this.setState({ stores: this.state.allStores, storeType: 'ALL_STORES', storeTypeLabel: this.resources.viewClosestStoresLabel, storeHeadingLabel: this.resources.cittaAllStoresLabel, openFirstDrawerComp: false });
            } else {
                this.setState({ stores: this.state.nearMeStores, storeType: 'NEAR_ME', storeTypeLabel: this.resources.viewAllStoresLabel, storeHeadingLabel: this.resources.yourClosestStoresLabel, openFirstDrawerComp: true });
            }
        }
    }

    // tslint:disable: react-a11y-anchors
    // tslint:disable: cyclomatic-complexity
    private _renderStoresList(): React.ReactNode {
        return (this.state.stores && this.state.stores.map((child: IDXC_StoreLocations, index: number) => {
            const streetaddress: '' | string[] | undefined = child.address && child.address.split('\n');
            const stockistsOf: '' | string[] | undefined = child.stockistOf && child.stockistOf.split('\n');
            return (
                <div className='citta-store' key={index}>
                    <Drawer
                        collapseProps={{
                            timeout: 0,
                            // tslint:disable: restrict-plus-operands
                            // tslint:disable: prefer-template
                            isOpen: (this.state.storeCategoryId === (child && child.storName || '') + index) || (index === 0 && this.state.openFirstDrawerComp) ? true : false
                        }}
                        toggleButtonText={child.storName || ''}
                        // tslint:disable: jsx-no-lambda
                        // tslint:disable: react-this-binding-issue
                        onToggle={() => this._changeStoreCategoryItem((child && child.storName || '') + index)}
                    >
                        <div className='citta-store-drawer' key={index} role='menuitem'>
                            <div className='citta-address-phone-email-map'>
                                <div className='citta-store-address'>
                                    {streetaddress && streetaddress.map((addressline: string, indexadd: number) =>
                                        (<div className={`citta-store-address-${indexadd}`} key={indexadd}>
                                            {addressline}
                                        </div>
                                        ))}
                                </div>
                                <div className='citta-store-phone'>
                                    {child.phone}
                                </div>
                                <div className='citta-store-email'>
                                    <a className='citta-store-email-link' href={`mailto:${child.emailAddress}`}>{child.emailAddress}</a>
                                </div>
                                <div className='citta-store-location-notes'>
                                    {child.locationNotes}
                                </div>
                                <div className='citta-store-map'>
                                    <a className='citta-store-map-link' href={child && child.googleMapLink ? child.googleMapLink : '#'} target='_blank'>View on map &gt;</a>
                                </div>
                            </div>
                            <div className='citta-store-storeHours'>
                                <div className='citta-store-storeHours-heading'>
                                    {this.resources.store_storeHoursHeading}
                                </div>
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnMonday!, this.resources.store_storeHoursMonday, child.storeHours.MondayOpenTime!, child.storeHours.MondayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnTuesday!, this.resources.store_storeHoursTuesday, child.storeHours.TuesdayOpenTime!, child.storeHours.TuesdayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnWednesday!, this.resources.store_storeHoursWednesday, child.storeHours.WednesdayOpenTime!, child.storeHours.WednesdayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnThursday!, this.resources.store_storeHoursThursday, child.storeHours.ThursdayOpenTime!, child.storeHours.ThursdayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnFriday!, this.resources.store_storeHoursFriday, child.storeHours.FridayOpenTime!, child.storeHours.FridayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnSaturday!, this.resources.store_storeHoursSaturday, child.storeHours.SaturdayOpenTime!, child.storeHours.SaturdayCloseTime!)}
                                {child && child.storeHours && this._renderStore(child.storeHours.IsClosedOnSunday!, this.resources.store_storeHoursSunday, child.storeHours.SundayOpenTime!, child.storeHours.SundayCloseTime!)}
                            </div>
                            <div className='citta-store-stockistsof'>
                                <div className='citta-store-stockistsof-heading'>
                                    {this.resources.store_stockistOfHeading}
                                </div>
                                <div className='citta-store-stockistsof-content'>
                                    {stockistsOf && stockistsOf.map((addressline: string, indexstockistsof: number) =>
                                        (<div className={`citta-store-stockistsof-${indexstockistsof}`} key={indexstockistsof}>
                                            {addressline}
                                        </div>))}
                                </div>
                            </div>
                        </div>
                    </Drawer>
                </div>
            );
        }));
    }

    private _secondsToTime = (seconds: number | undefined): string | undefined => {
        if (seconds === undefined) {
            return undefined;
        } else {
            let hours = Math.floor(seconds / (60 * 60));
            const ampm = hours >= 12 ? 'pm' : 'am';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const divisorForMinutes = seconds % (60 * 60);
            const minutes =
                Math.floor(divisorForMinutes / 60) < 10 ? `0${Math.floor(divisorForMinutes / 60)}` : Math.floor(divisorForMinutes / 60);
            return `${hours}.${minutes} ${ampm}`;
        }
    };

    private _renderStore(isClosed: boolean, day: string, openTime: number, closeTime: number): JSX.Element | null {
        const openFrom: string | undefined = this._secondsToTime(openTime);
        const openTo: string | undefined = this._secondsToTime(closeTime);
        return (
            <div>
                {isClosed ?
                    <div className='citta-store-storeHours-content'>
                        <div className='citta-store-storeHours-content-day'> {day}</div>
                        <div className='citta-store-storeHours-content-hours'>{this.resources.store_storeHoursClosed}</div>
                    </div> :
                    <div className='citta-store-storeHours-content'>
                        <div className='citta-store-storeHours-content-day'> {day}</div>
                        <div className='citta-store-storeHours-content-hours'>{openFrom} - {openTo}</div>
                    </div>}
            </div>
        );
    }

    // tslint:disable-next-line: no-any
    private _changeStoreCategoryItem(storeCategoryId: string): any {
        if (this.state.storeCategoryId !== storeCategoryId) {
            this.setState({ storeCategoryId: storeCategoryId, openFirstDrawerComp: false });
        } else {
            this.setState({ storeCategoryId: '', openFirstDrawerComp: false });
        }
    }
}

export default CittaStoreLocations;